<template>
  <MDBCard class="border-0">
    <CategoryTop small :title="mainTitle" :breadcrumbs-parent-routes="['account']"
      :description="$t('staff.dashboard.description')" content-class="px-4 mt-4" left-col="8" right-col="4">
      <template #right>
        <Loader v-if="loaders.lastImport" :show="loaders.lastImport" />
        <DashboardCard v-if="lastImport && !loaders.lastImport">
          <template v-slot:content>
            <div v-html="$t('staff.dashboard.lastImport', {
              date: formatLastImportDate(lastImport),
            })
              "></div>
          </template>
        </DashboardCard>
      </template>
    </CategoryTop>
    <MDBRow class="px-4">
      <MDBCol cols="12" md="6" xl="3" class="d-flex">
        <Loader v-if="loaders.overview" :show="loaders.overview" />
        <DashboardCard v-else :title="$t('staff.dashboard.overview.title')"
          :description="$t('staff.dashboard.overview.description')" class="mb-4">
          <template v-if="overview" v-slot:table>
            <MDBTable borderless class="table-dashboard">
              <thead>
                <tr>
                  <th>{{ $t("staff.dashboard.overview.all") }}</th>
                  <th>{{ overview.all }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t("staff.dashboard.overview.month") }}</td>
                  <td>{{ overview.per_month }}</td>
                </tr>
                <tr>
                  <td>{{ $t("staff.dashboard.overview.year") }}</td>
                  <td>{{ overview.per_year }}</td>
                </tr>
              </tbody>
            </MDBTable>
          </template>
        </DashboardCard>
      </MDBCol>
      <MDBCol cols="12" md="6" xl="3" class="d-flex">
        <Loader v-if="loaders.points" :show="loaders.points" />
        <DashboardCard :title="pointsWidgetConfig.title" :description="pointsWidgetConfig.description" class="mb-4"
          v-else>
          <template v-if="points" v-slot:table>
            <MDBTable borderless class="table-dashboard">
              <thead>
                <tr v-if="pointsWidgetFirstRow">
                  <th>{{ pointsWidgetFirstRow.label }}</th>
                  <th>{{ points?.[pointsWidgetFirstRow.field] }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pointsWidgetBodyRows" :key="row.field">
                  <td>{{ row.label }}</td>
                  <td>{{ points?.[row.field] }}</td>
                </tr>
              </tbody>
            </MDBTable>
          </template>
        </DashboardCard>
      </MDBCol>
      <MDBCol cols="12" md="6" xl="3" class="d-flex" v-if="tenantConfig.sellerDashboardShowKickbacks">
        <Loader v-if="loaders.kickbacks" :show="loaders.kickbacks" />
        <DashboardCard v-else :title="$t('kickback.title')" :description="$t('staff.dashboard.kickback.description')"
          class="mb-4">
          <template v-if="kickbacks" v-slot:table>
            <MDBTable borderless class="table-dashboard">
              <thead>
                <tr>
                  <th>{{ $t("staff.dashboard.credits.prev_month") }}</th>
                  <th>{{ kickbacks.prev_month }} &euro;</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.month") }}</td>
                  <td>{{ kickbacks.month }} &euro;</td>
                </tr>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.year") }}</td>
                  <td>{{ kickbacks.year }} &euro;</td>
                </tr>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.prev_year") }}</td>
                  <td>{{ kickbacks.prev_year }} &euro;</td>
                </tr>
              </tbody>
            </MDBTable>
          </template>
        </DashboardCard>
      </MDBCol>
      <MDBCol cols="12" md="6" xl="3" class="d-flex" v-if="tenantConfig.sellerDashboardShowTyres">
        <Loader v-if="loaders.tyres" :show="loaders.tyres" />
        <DashboardCard v-else :title="$t('staff.dashboard.tyres.title')"
          :description="$t('staff.dashboard.tyres.description')" class="mb-4">
          <template v-if="tyres" v-slot:table>
            <MDBTable borderless class="table-dashboard">
              <thead>
                <tr>
                  <th>{{ $t("staff.dashboard.credits.prev_month") }}</th>
                  <th>{{ tyres.prev_month }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.month") }}</td>
                  <td>{{ tyres.month }}</td>
                </tr>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.yearTyre") }}</td>
                  <td>{{ tyres.year }}</td>
                </tr>
                <tr>
                  <td>{{ $t("staff.dashboard.credits.prev_yearTyre") }}</td>
                  <td>{{ tyres.prev_year }}</td>
                </tr>
              </tbody>
            </MDBTable>
          </template>
        </DashboardCard>
      </MDBCol>
    </MDBRow>
    <MDBRow class="px-4 pb-4">
      <MDBCol col="12">
        <DashboardCard :title="$t('staff.dashboard.details.title')"
          :description="$t('staff.dashboard.details.description')">
          <template v-slot:search>
            <div class="d-flex flex-column justify-content-end align-items-end">
              <div class="w-100">
                <MDBInput v-model="query" :label="$t('global.buttons.toSearch')" />
              </div>
              <div class="w-100" v-if="tenantConfig.sellerDashboardShowYearSearch">
                <MDBInput v-model="year" :label="$t('global.year')" type="number" />
              </div>
              <div>
                <MDBBtn color="danger" @click="search" :disabled="loading || searchLoading">{{
                  $t("global.buttons.toSearch")
                }}</MDBBtn>
              </div>
            </div>
          </template>
          <template v-slot:table>
            <div class="overflow-x-auto">
              <MDBTable borderless class="table-dashboard seller-dashboard-details-table"
                :style="{ '--seller-dashboard-details-columns': visibleDetailsColumnsCount }">
                <thead>
                  <tr>
                    <th v-for="(head, id) in detailsHeaders" :key="`key_${id}`" :class="`${head.show ? '' : 'd-none'}`">
                      <div class="table-header-stats">
                        <span>{{ head.label }}</span>
                        <img v-if="head.field != 'details'" :src="require('@/assets/img/icons/filter_btn.svg')"
                          alt="filter_btn" class="icon-container--small cursor--pointer" @click="newSort(head.field)" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody :class="{ 'details-loading': loaders.details }">
                  <tr v-for="(user, key) in users" :key="`user_${key}`" class="cursor--pointer staffWsh-table-row">
                    <td v-for="(head, id) in detailsHeaders" :key="`item_${id}`"
                      :class="`${head.show ? '' : 'd-none'}`">
                      <div class="loader" v-if="loaders.details && selectedIndex == key"></div>
                      <div @click="openDetailsModal(key, user)" v-else>
                        <template v-if="head.field == 'details'">
                          <div class="text-decoration-underline cursor--pointer">
                            {{ $t("account.profile.details") }}
                          </div>
                        </template>
                        <template v-else>
                          {{ user[head.field] }}
                        </template>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </MDBTable>
            </div>
            <div class="load-more d-flex justify-content-center">
              <button @click="loadMore" class="btn btn-danger text-uppercase px-5" :disabled="loading" type="button">
                {{ $t("global.buttons.loadMore") }}
              </button>
              <button @click="loadAll" class="btn btn-danger text-uppercase px-5"
                v-if="tenantConfig.sellerDashboardShowLoadAll" :disabled="loading || exportLoading" type="button">
                {{ $t("global.buttons.all") }}
              </button>

              <DownloadAttribute v-if="tenantConfig.sellerDashboardShowAttributeDownload" />

              <downloadexcel v-if="tenantConfig.sellerDashboardShowExcelExport"
                :class="['btn btn-danger text-uppercase px-5', { disabled: loading || exportLoading }]"
                :style="{ pointerEvents: loading || exportLoading ? 'none' : 'auto', opacity: loading || exportLoading ? 0.65 : 1 }"
                :fetch="loadAll" :data="users" :fields="exportLabels" type="xlsx" name="stats.xlsx">
                {{ $t("staff.dashboard.exports.downloadExcel") }}
              </downloadexcel>
            </div>
          </template>
        </DashboardCard>
      </MDBCol>
    </MDBRow>
  </MDBCard>
  <MDBModal v-model="modal" tabindex="-1" centered class="modal-xxl" staticBackdrop>
    <div class="d-flex justify-content-end px-3 py-3">
      <span class="cursor--pointer" @click="closeDetails()">X</span>
    </div>
    <Loader v-if="modalLoading" :show="true" />
    <PointsOverviewModal v-else-if="detailsId && userDetails && selectedProfile" :user_id="detailsId"
      :user_data="userDetails" :profile_data="selectedProfile" :wsh_data="wshSelect" />
  </MDBModal>
</template>

<script setup>
import {
  MDBCard,
  MDBCol,
  MDBRow,
  MDBInput,
  MDBBtn,
  MDBTable,
  MDBModal,
} from "mdb-vue-ui-kit";
import downloadexcel from "vue-json-excel3";
import moment from "moment";

import { useStore } from "vuex";
import { ref, computed, toRaw, onMounted } from "vue";

import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useI18n } from "vue-i18n";

import { ProfileService } from "@/services/profile.service";
import { StaffService } from "@/services/staff.service";

import CategoryTop from "@/components/General/CategoryTop/index.vue";
import DashboardCard from "@/components/User/Seller/DashboardCard.vue";
import PointsOverviewModal from "./PointsOverviewModal.vue";
import Loader from "@/components/General/Loader/index.vue";
import DownloadAttribute from "@/components/DownloadAttribute.vue";
import dates from "@/plugins/dates";
import { useTenantConfig } from "@/composables/useTenantConfig";

const store = useStore();
const { tenantConfig } = useTenantConfig();
const { t } = useI18n();

const me = computed(() => store.getters.getMe);
const fullName = ref("");
const selectedIndex = ref(null);

const loaders = ref({
  overview: true,
  points: true,
  kickbacks: tenantConfig.value.sellerDashboardShowKickbacks,
  lastImport: true,
  tyres: tenantConfig.value.sellerDashboardShowTyres,
  details: false,
});

const mainTitle = computed(() => {
  if (tenantConfig.value.sellerDashboardFullNameTitle) {
    return `${fullName.value} - ${t("staff.dashboard.title")}`;
  } else {
    return t("staff.dashboard.title");
  }
});
const tenantId = computed(() => store.getters.tenantId ?? "DEFAULT");
const currentLocale = computed(() => store.getters.getCurrentLocale);
const loading = computed(() => store.getters.getPending);

const sortData = ref({
  field: "",
  order: "asc",
});

const page = ref(0);
const users = ref([]);
const query = ref("");
const overview = ref(null);
const points = ref(null);
const lastImport = ref(null);
const kickbacks = ref(null);
const tyres = ref(null);
const searchLoading = ref(false);
const exportLoading = ref(false);
const modal = ref(false);
const modalLoading = ref(false);
const detailsId = ref(null);
const userDetails = ref(null);
const selectedProfile = ref(null);
const wshSelect = ref([]);
const cachedWshSelect = computed(() => {
  const currentWshSelect = store.getters.getWshSelect;
  return Array.isArray(currentWshSelect) ? currentWshSelect : [];
});
const exportLabels = ref([]);
const year = ref('')

const pointsWidgetConfig = computed(() => {
  const pointsTranslationVariantByLocale = {
    "de-DE": "default",
    "de-CH": "default",
    "fr-CH": "default",
    "it-CH": "default",
    "nl-NL": "nl",
  };
  const translationVariant =
    pointsTranslationVariantByLocale[currentLocale.value] ?? "default";

  const pointsTranslationsByVariant = {
    nl: {
      titleKey: "staff.dashboard.credits.tenant.nl.title",
      descriptionKey: "staff.dashboard.credits.tenant.nl.description",
      labels: {
        prev_month: "staff.dashboard.credits.tenant.nl.prev_month",
        month: "staff.dashboard.credits.tenant.nl.month",
        year: "staff.dashboard.credits.tenant.nl.year",
        prev_year: "staff.dashboard.credits.tenant.nl.prev_year",
      },
    },
    default: {
      titleKey: "staff.dashboard.credits.title",
      descriptionKey: "staff.dashboard.credits.description",
      labels: {
        prev_month: "staff.dashboard.credits.prev_month",
        month: "staff.dashboard.credits.month",
        year: "staff.dashboard.credits.year",
        prev_year: "staff.dashboard.credits.prev_year",
        allStaffDealersSumPoints: "staff.dashboard.credits.allDealers",
      },
    },
  };

  const pointsStructureByTenant = {
    DE: {
      rows: ["prev_month", "month", "prev_year", "allStaffDealersSumPoints"],
    },
    AT: {
      rows: ["prev_month", "month", "prev_year", "allStaffDealersSumPoints"],
    },
    CH: {
      rows: ["prev_month", "month", "prev_year", "allStaffDealersSumPoints"],
    },
    FR: {
      rows: ["prev_month", "month"],
    },
    NL: {
      rows: ["prev_month", "month", "year", "prev_year"],
    },
    default: {
      rows: [
        "prev_month",
        "month",
        ...(tenantConfig.value.sellerDashboardShowYearPoints
          ? ["year", "prev_year"]
          : []),
        ...(tenantConfig.value.sellerDashboardShowDealerTotals
          ? ["allDealersSumPoints", "allStaffDealersSumPoints"]
          : []),
      ],
    },
  };

  const selectedStructure =
    pointsStructureByTenant[tenantId.value] ?? pointsStructureByTenant.default;
  const selectedTranslations =
    pointsTranslationsByVariant[translationVariant] ??
    pointsTranslationsByVariant.default;

  return {
    title: t(selectedTranslations.titleKey),
    description: t(selectedTranslations.descriptionKey),
    rows: selectedStructure.rows.map((field) => ({
      field,
      label: t(
        selectedTranslations.labels[field] ??
        pointsTranslationsByVariant.default.labels[field]
      ),
    })),
  };
});
const pointsWidgetFirstRow = computed(() => pointsWidgetConfig.value.rows[0] ?? null);
const pointsWidgetBodyRows = computed(() => pointsWidgetConfig.value.rows.slice(1));

const detailsHeaders = ref([]);
const visibleDetailsColumnsCount = computed(() => {
  const visibleHeaders = detailsHeaders.value.filter((head) => head.show !== false);
  return Math.max(visibleHeaders.length - 1, 1);
});

const formatLastImportDate = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    const parsedDotDate = moment.utc(value.trim(), "DD.MM.YYYY", true);
    if (parsedDotDate.isValid()) {
      return parsedDotDate.local().format("YYYY-MM-DD");
    }
  }

  return value ? moment.utc(value).local().format("YYYY-MM-DD") : "";
};

const normalizeUsersBody = (body) => {
  if (Array.isArray(body)) {
    return body;
  }
  if (body && typeof body === "object") {
    return Object.values(toRaw(body));
  }
  return [];
};

const search = async () => {
  searchLoading.value = true;
  try {
    const usersResponse = await StaffService.staffSearch(query.value, 0, year.value);
    users.value = normalizeUsersBody(usersResponse.body);
    detailsHeaders.value = usersResponse.headers;
  } finally {
    searchLoading.value = false;
  }
};

const ensureWshSelectLoaded = async () => {
  if (cachedWshSelect.value.length > 0) {
    wshSelect.value = cachedWshSelect.value;
    return cachedWshSelect.value;
  }

  const loadedWshSelect = await store.dispatch("fetchWshSelect");
  wshSelect.value = Array.isArray(loadedWshSelect) ? loadedWshSelect : [];

  return wshSelect.value;
};

const openDetailsModal = async (key, user) => {
  if (loaders.value.details) {
    return;
  }
  loaders.value.details = true;
  modalLoading.value = true;
  modal.value = true;
  selectedIndex.value = key;
  detailsId.value = user.details;
  userDetails.value = user;
  selectedProfile.value = null;
  wshSelect.value = [];

  try {
    const response = await ProfileService.userProfileData(user.details);
    selectedProfile.value = response.data.data;

    await ensureWshSelectLoaded();
    await store.dispatch("loadPointsByCsNumber", user.details);
  } finally {
    modalLoading.value = false;
    loaders.value.details = false;
  }
};

const newSort = (field) => {
  if (!field) {
    return;
  }

  if (sortData.value.field == field) {
    sortData.value.order = sortData.value.order === "asc" ? "desc" : "asc";
  } else {
    sortData.value.order = "asc";
  }
  sortData.value.field = field;
  users.value = [...normalizeUsersBody(users.value)].sort(
    dates.compareSortValues(sortData.value.field, sortData.value.order)
  );
};

const loadMore = async () => {
  page.value += 1;

  const usersResponse = await StaffService.staffSearch(query.value, page.value, year.value);

  if (usersResponse) {
    users.value.push(...normalizeUsersBody(usersResponse.body));
    newSort(sortData.value.field);
  }
};

const loadAll = async () => {
  if (exportLoading.value) {
    return users.value;
  }

  exportLoading.value = true;
  toast(t("toast.waitMoment"), {
    type: "success",
    theme: "colored",
    transition: toast.TRANSITIONS.SLIDE,
    position: toast.POSITION.BOTTOM_RIGHT,
  });

  try {
    const usersResponse = await StaffService.staffSearchAll(query.value, year.value);

    if (usersResponse?.body) {
      users.value = normalizeUsersBody(usersResponse.body);
    }

    if (usersResponse?.headers) {
      detailsHeaders.value = usersResponse.headers;
    }

    toast(t("toast.downloadFinished"), {
      type: "success",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });

    return users.value;
  } finally {
    exportLoading.value = false;
  }
};

const closeDetails = () => {
  selectedIndex.value = null;
  detailsId.value = null;
  userDetails.value = null;
  selectedProfile.value = null;
  wshSelect.value = [];
  modalLoading.value = false;
  modal.value = false;
};

onMounted(async () => {
  fullName.value =
    me.value.profile.forename + " " + me.value.profile.name;
  const usersResponse = await StaffService.staffSearch();
  users.value = normalizeUsersBody(usersResponse.body);
  detailsHeaders.value = usersResponse.headers;

  exportLabels.value = detailsHeaders.value.reduce((acc, current) => {
    // Ensure 'field' property exists and has a value
    if (current.field) {
      acc[current.label] = current.field;
    } else {
      console.warn(`Skipping object without 'field' property:`, current);
    }
    return acc;
  }, {});

  const staffStats = await StaffService.staffStats(me.value.id)

  overview.value = staffStats.overview;
  loaders.value.overview = false;
  lastImport.value = staffStats.lastImport;
  loaders.value.lastImport = false;
  points.value = staffStats.points;
  loaders.value.points = false;

  store.dispatch("addAllDealersNumber", overview.value.all);

  if (tenantConfig.value.sellerDashboardShowKickbacks) {
    kickbacks.value = staffStats.kickbacks;
    loaders.value.kickbacks = false;
  }

  if (tenantConfig.value.sellerDashboardShowTyres) {
    tyres.value = staffStats.tyres;
    loaders.value.tyres = false;
  }

  if (tenantConfig.value.sellerDashboardShowExports) {
    store.dispatch("loadExportsByUserID", me.value.id);
  }
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";
@import "@/styles/table_dashboard.scss";
@import "@/styles/_dashboard_loader.scss";
@import "@/styles/_dashboard_details_loading.scss";

.pointer {
  cursor: pointer;
}

.seller-dashboard-details-table {

  th:first-child,
  td:first-child {
    width: 18%;
    min-width: 180px;
  }

  th:not(:first-child),
  td:not(:first-child) {
    width: calc(82% / var(--seller-dashboard-details-columns, 1));
  }
}
</style>
